import { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  getAvailableOrganizations,
  mapApiOrganizations,
} from 'src/services/organizations.service';
import { getFreshToken } from 'src/utils/get-fresh-token';
import { useAppSelector } from 'src/hooks/useStore';
import type { OrganizationWithLogo } from 'src/hooks/useOrganizations';

/** Full organization catalog (admin). GET /organizations */
export const useAllOrganizations = () => {
  const { getIdTokenClaims } = useAuth0();
  const { liveData } = useAppSelector(state => state.sliceTransaction);
  const [organizations, setOrganizations] = useState<OrganizationWithLogo[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganizations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = await getFreshToken(getIdTokenClaims);
      if (!token) {
        setError('Failed to get authentication token');
        setIsLoading(false);
        return;
      }

      const response = await getAvailableOrganizations(token, liveData);
      const orgsArray = response.data || response.organizations || [];
      setOrganizations(mapApiOrganizations(orgsArray));
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch organizations';
      setError(message);
      setOrganizations([]);
    } finally {
      setIsLoading(false);
    }
  }, [getIdTokenClaims, liveData]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  return { organizations, isLoading, error, refetch: fetchOrganizations };
};
